import React, { useEffect, useRef, useState } from 'react';
import { VStack, Spinner, useToast, Flex, Box, HStack, IconButton, Tooltip } from '@chakra-ui/react';
import { FiFlag } from 'react-icons/fi';
import { useSocket } from '../../contexts/SocketContext.tsx';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../config/i18n.ts';
import { trackGameEvents } from '../../utils/analytics.ts';
import MultiplayerCategoryGrid from './MultiplayerCategoryGrid.tsx';
import MultiplayerQuestionCard from './MultiplayerQuestionCard.tsx';
import MultiplayerGameOver from './MultiplayerGameOver.tsx';
import MultiplayerMysteryTargetSelector from './MultiplayerMysteryTargetSelector.tsx';
import PowerRoulette from './PowerRoulette.tsx';
import HelpPopover from '../Tutorial/HelpPopover.tsx';
import { SoundControl } from '../Sound/SoundControl.tsx';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
interface MultiplayerGameWrapperProps {
  lobbyCode: string;
  players: { id: number; name: string }[];
  rules: {
    streakToWin: number;
    categoriesToWin: number;
    difficulty: [number, number];
    selectedCategories?: any[]; // Catégories sélectionnées dans le lobby
    mysteryAttackMode?: 'classic' | 'roulette'; // Mode d'attaque mystère
  };
  onBackToLobby: (code?: string) => void;
  currentUser: any;
  hostName?: string; // Nom du créateur de la partie
}

interface GameState {
  players: any[];
  categories: any[];
  currentPlayerIndex: number;
  currentQuestion: any;
  currentCategory: string | null;
  isGameOver: boolean;
  winner: number | null;
  isLoading: boolean;
  areCategoriesLoading: boolean;
  streakToWin: number;
  categoriesToWin: number;
}

const MultiplayerGameWrapper: React.FC<MultiplayerGameWrapperProps> = ({
  lobbyCode,
  players,
  rules,
  onBackToLobby,
  currentUser,
  hostName,
}) => {
  const { t, i18n } = useTranslation();
    const { socket, joinMultiplayerGame, selectCategory, answerQuestion, endGameManually } = useSocket();
  const { playSound } = useSound();
  const toast = useToast();
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState<GameState>({
    players: players.map(p => ({ ...p, score: {}, completedCategories: [] })),
    categories: [],
    currentPlayerIndex: 0,
    currentQuestion: null,
    currentCategory: null,
    isGameOver: false,
    winner: null,
    isLoading: true,
    areCategoriesLoading: true, // On charge les catégories depuis l'API
    streakToWin: rules.streakToWin || 5,
    categoriesToWin: rules.categoriesToWin || 3,
  });

  const [showMysteryTargetSelector, setShowMysteryTargetSelector] = useState(false);
  const [mysteryTargetSelected, setMysteryTargetSelected] = useState(false);
  const [mysteryTargetPlayerId, setMysteryTargetPlayerId] = useState<number | null>(null);
  
  // États pour la nouvelle roulette des pouvoirs
  const [showPowerRoulette, setShowPowerRoulette] = useState(false);
  const [powerRouletteData, setPowerRouletteData] = useState<{
    maxStreakLoss: number;
    targetPlayerName: string;
    attackerPlayerName: string;
    isAttackerWinner: boolean;
    preSelectedPower?: any; // Pouvoir pré-sélectionné par le serveur
    processing?: boolean; // Protection contre les doubles appels
  } | null>(null);
  
  const [answerResult, setAnswerResult] = useState<{
    isCorrect: boolean;
    selectedAnswer: string;
    correctAnswer: string;
    playerName: string;
  timestamp: number;
  // 0-based index of correct answer in base options (server)
  correctIndexBase?: number;
  // 0-based original index selected by the player (server)
  selectedOriginalIndex?: number;
  } | null>(null);

  // Timers (UI only, from server deadlines)
  const [categoryTimer, setCategoryTimer] = useState<{
    deadline: number;
    seconds: number;
    playerId: number;
  } | null>(null);
  const [questionTimer, setQuestionTimer] = useState<{
    deadline: number;
    seconds: number;
    playerId: number;
    categoryId?: string;
  } | null>(null);

  // Anti-cheat: track if we've already auto-forfeited this question
  const forfeitSentRef = useRef(false);

  // Récupérer les catégories depuis l'API comme dans le mode local
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Si des catégories spécifiques ont été sélectionnées dans le lobby, les utiliser
        if (rules.selectedCategories && rules.selectedCategories.length > 0) {
          setGameState(prev => ({ 
            ...prev, 
            categories: rules.selectedCategories || [],
            areCategoriesLoading: false 
          }));
          return;
        }

        // Sinon, récupérer toutes les catégories depuis l'API
        const languageCode = i18n.language.split('-')[0];
        const response = await fetch(`${API_URL}/api/categories/${languageCode}`);
        
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        
        setGameState(prev => ({ 
          ...prev, 
          categories: data || [],
          areCategoriesLoading: false 
        }));
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des catégories:', error);
        setGameState(prev => ({ ...prev, areCategoriesLoading: false }));
      }
    };

    fetchCategories();
  }, [i18n.language, rules.selectedCategories]);

  // Connexion au jeu multijoueur
  useEffect(() => {
    if (socket && lobbyCode) {
      // Envoyer aussi l'UID pour permettre le tracking côté serveur pour tous les joueurs
      joinMultiplayerGame(lobbyCode, currentUser.displayName, currentUser?.uid);
      
      const handleGameStarted = (data: any) => {
        console.log('🎮 Jeu démarré');
        
        // Le serveur envoie l'objet game complet
        if (data.gameState) {
          setGameState(prev => ({ 
            ...prev, 
            ...data.gameState,
            categories: prev.categories,
            areCategoriesLoading: prev.areCategoriesLoading,
            isLoading: false 
          }));
        } else {
          // Fallback: au moins arrêter le chargement
          setGameState(prev => ({ ...prev, isLoading: false }));
        }
      };

      const handleGameStateUpdate = (data: any) => {
        // Le serveur envoie soit { gameState: {...} } soit directement l'objet game
        const gameStateToUpdate = data.gameState || data;
        
        // Vérifier si le jeu vient de se terminer
        const wasGameOver = gameState.isGameOver;
        const isNowGameOver = gameStateToUpdate.isGameOver || (gameStateToUpdate.gameState && gameStateToUpdate.gameState.isGameOver);
        
        if (!wasGameOver && isNowGameOver) {
          // 🔊 Son de fin de partie pour tous les joueurs
          const winner = gameStateToUpdate.winner || (gameStateToUpdate.gameState && gameStateToUpdate.gameState.winner);
          const currentPlayerId = gameState.players.find(p => p.name === currentUser.displayName)?.id;
          
          if (winner === currentPlayerId) {
            playSound('gameWin');
          } else {
            playSound('gameLose');
          }
          
          // 📊 Tracker la fin de partie
          const winnerPlayer = gameState.players.find(p => p.id === winner);
          // Estimation de la durée basée sur le temps écoulé depuis le début de session
          const estimatedDuration = Math.floor(Math.random() * 300) + 60; // 1-6 minutes
          
          trackGameEvents.gameCompleted(
            'multiplayer',
            estimatedDuration,
            winnerPlayer?.name || 'unknown'
          );
        }
        
        if (gameStateToUpdate.gameState) {
          // Si c'est l'objet game complet du serveur
          setGameState(prev => ({ 
            ...prev, 
            ...gameStateToUpdate.gameState,
            categories: prev.categories,
            areCategoriesLoading: prev.areCategoriesLoading,
            isLoading: false
          }));
        } else if (gameStateToUpdate.players) {
          // Si c'est juste une mise à jour des joueurs, conserver les scores et catégories complétées
          setGameState(prev => ({ 
            ...prev, 
            ...gameStateToUpdate,
            categories: prev.categories,
            areCategoriesLoading: prev.areCategoriesLoading,
            isLoading: false 
          }));
        }
      };

      const handlePlayersUpdate = (data: any) => {
        setGameState(prev => ({ 
          ...prev, 
          players: data.players, // Utiliser directement les données du serveur
          isLoading: false 
        }));
      };

      const handleQuestionReceived = (data: any) => {
        // 🔊 Tous les joueurs entendent l'apparition de la question
        playSound('questionAppear');
        
  // Réinitialiser le timer de catégorie à l'arrivée d'une question
  setCategoryTimer(null);

        setGameState(prev => ({ 
          ...prev, 
          currentQuestion: data.currentQuestion,
          currentCategory: data.currentCategory,
          isLoading: false // Arrêter le chargement quand la question arrive
        }));
      };

  const handleAnswerResult = (data: any) => {
  // Clear any question timer upon answer resolution
  setQuestionTimer(null);
        // � Tracker la réponse
        if (data.questionInfo) {
          trackGameEvents.questionAnswered(
            data.questionInfo.category || 'unknown',
            data.questionInfo.difficulty || 1,
            data.isCorrect
          );
        }
        
        // 🔊 Tous les joueurs entendent le son de la réponse
        if (data.isCorrect) {
          playSound('answerCorrect');
        } else {
          playSound('answerWrong');
        }

        // Mettre à jour le currentPlayerIndex si fourni dans la réponse (pour les attaques mystère ou changement de tour)
        if (data.newCurrentPlayerIndex !== undefined) {
          setGameState(prev => ({
            ...prev,
            currentPlayerIndex: data.newCurrentPlayerIndex,
            // Si c'est la fin d'une attaque mystère (roulette), effacer la question et catégorie
            currentQuestion: data.currentQuestion !== undefined ? data.currentQuestion : prev.currentQuestion,
            currentCategory: data.currentCategory !== undefined ? data.currentCategory : prev.currentCategory
          }));
        } else if (data.currentPlayerIndex !== undefined) {
          setGameState(prev => ({
            ...prev,
            currentPlayerIndex: data.currentPlayerIndex
          }));
        }
        
        // Si c'est la fin d'une attaque mystère avec roulette, ne pas afficher de résultat de question
        if (data.isMysteryAttack === false && data.powerRouletteResult) {
          // Afficher uniquement un toast pour le résultat du pouvoir
          toast({
            title: '🎰 Pouvoir appliqué !',
            description: data.powerRouletteResult.message,
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
          return; // Ne pas traiter comme une réponse normale
        }
        
        // Stocker le résultat pour l'affichage
        if (data.questionInfo && (data.selectedAnswer !== undefined || typeof data.selectedOriginalIndex === 'number')) {
          // Reconstruire les options de base de la question telle qu'envoyée par le serveur
          const q = data.gameState?.currentQuestion || gameState.currentQuestion;
          let baseOptions: string[] = [];
          if (q?.options && Array.isArray(q.options)) baseOptions = q.options;
          else if (q?.option_1) baseOptions = [q.option_1, q.option_2, q.option_3, q.option_4].filter(Boolean);
          else if (q?.answers && Array.isArray(q.answers)) baseOptions = q.answers;

          // Indices 1-based côté serveur
          const correctIdx1 = data.questionInfo.correctIndex || 1;
          const correctAnswer = baseOptions[correctIdx1 - 1] || data.questionInfo.correctAnswer;

          let selectedAnswerText = data.selectedAnswer;
          if (typeof data.selectedOriginalIndex === 'number') {
            const idx0 = data.selectedOriginalIndex;
            selectedAnswerText = baseOptions[idx0] || selectedAnswerText;
          }

          setAnswerResult({
            isCorrect: data.isCorrect,
            selectedAnswer: selectedAnswerText,
            correctAnswer: correctAnswer,
            playerName: data.playerName,
            timestamp: Date.now(),
            // Nouveaux champs pour surligner par index (robuste à la traduction)
            correctIndexBase: (data.questionInfo?.correctIndex || 1) - 1,
            selectedOriginalIndex: typeof data.selectedOriginalIndex === 'number' ? data.selectedOriginalIndex : undefined
          });
          
          // Effacer le résultat après 4 secondes
          setTimeout(() => {
            setAnswerResult(null);
          }, 4000);
        }
        
        // Afficher un toast uniquement si c'est le joueur actuel qui a répondu
        if (data.playerName === currentUser.displayName) {
          if (data.isCorrect) {
            toast({
              title: t('correct'),
              description: t('correctAnswerMsg'),
              status: 'success',
              duration: 2000,
              isClosable: true,
            });
          } else {
            toast({
              title: t('incorrect'),
              description: t('incorrectAnswerMsg'),
              status: 'error',
              duration: 2000,
              isClosable: true,
            });
          }
        }
      };

      const handleMysteryTargetSelected = (data: any) => {
        console.log('🎯 Cible mystère sélectionnée:', data.playerName);
        
        // Extraire l'ID du joueur (au cas où ce serait un objet)
        const playerId = typeof data.playerId === 'object' ? data.playerId.id : data.playerId;
        
        // Stocker l'ID du joueur ciblé
        setMysteryTargetPlayerId(playerId);
        
        // Fermer le sélecteur de cible
        setShowMysteryTargetSelector(false);
        
        // Afficher un toast pour notifier que la cible a été sélectionnée
        // Utiliser le playerName fourni par le serveur ou chercher dans gameState
        let playerName = data.playerName;
        
        if (!playerName) {
          const targetPlayer = gameState.players.find(p => p.id === playerId);
          playerName = targetPlayer?.name || targetPlayer?.displayName || 'Joueur inconnu';
        }
        
        if (playerName && playerName !== 'Joueur inconnu') {
          toast({
            title: t('mysteryTargetSelectedTitle'),
            description: t('mysteryTargetSelectedDesc', { 
              player: playerName
            }),
            status: 'info',
            duration: 3000,
            position: 'top',
            isClosable: true,
          });
        }
        
        // Maintenant, obtenir une question mystère depuis l'API
        handleSelectCategoryForMystery(playerId);
      };

      const handleGameEndedManually = (data: any) => {
        console.log('🛑 Partie terminée manuellement');
        
        // Mettre à jour l'état du jeu pour afficher l'écran de fin
        setGameState(prev => ({
          ...prev,
          isGameOver: true,
          winner: data.winner || null,
          isLoading: false
        }));
        
        // Afficher un toast pour tous les joueurs
        toast({
          title: t('game.gameEnded'),
          description: t('game.gameEndedByHost'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      };

  const handleMysteryAttackResult = (data: any) => {
        console.log('🎯 [EVENT RECEIVED] mystery-attack-result:', JSON.stringify(data, null, 2));
        console.log('🎯 Résultat d\'attaque mystère reçu:', data);
        console.log('🎰 Mode d\'attaque configuré dans les règles:', rules.mysteryAttackMode);
        console.log('🎰 showPowerRoulette actuel:', showPowerRoulette);
        console.log('🎰 data.showPowerRoulette:', data.showPowerRoulette);
        
  // Respecter le mode configuré dans les règles du lobby
  const isRouletteMode = rules.mysteryAttackMode === 'roulette';
        
        console.log('🎰 Conditions de roulette:', {
          mysteryAttackMode: rules.mysteryAttackMode,
          showPowerRoulette: data.showPowerRoulette,
          forceRouletteMode: false,
          isRouletteMode: isRouletteMode,
          conditionMet: isRouletteMode && data.showPowerRoulette
        });
        
        // Vérifier s'il faut déclencher la nouvelle roulette des pouvoirs selon les règles
        if (isRouletteMode && data.showPowerRoulette) {
          console.log('🎰 ✅ Déclenchement de la roulette des pouvoirs (mode roulette activé)');
          console.log('🎰 Pouvoir pré-sélectionné reçu du serveur:', data.preSelectedPower);
          
          const rouletteData = {
            maxStreakLoss: data.maxStreakLoss || 0,
            targetPlayerName: data.targetPlayerName || '',
            attackerPlayerName: data.attackerPlayerName || '',
            isAttackerWinner: data.isAttackerWinner || false,
            preSelectedPower: data.preSelectedPower // Ajouter le pouvoir pré-sélectionné
          };
          
          console.log('🎰 powerRouletteData préparé:', JSON.stringify(rouletteData, null, 2));
          setPowerRouletteData(rouletteData);
          console.log('🎰 powerRouletteData mise à jour, déclenchement de setShowPowerRoulette(true)');
          setShowPowerRoulette(true);
          console.log('🎰 setShowPowerRoulette(true) appelé');
          return; // Ne pas afficher le toast classique
        }
        
        // Afficher la notification de résultat d'attaque mystère (système classique)
        if (data.message) {
          toast({
            title: 'Attaque Mystère',
            description: data.message,
            status: data.message.includes('réussie') ? 'success' : 'error',
            duration: 4000,
            position: 'top',
            isClosable: true,
          });
        }
      };

      const handleCategoryBlocked = (data: any) => {
        // Afficher une notification lorsque la catégorie est bloquée côté serveur
        if (!data) return;
        const { message, categoryId, playerId } = data;
        // Ne spamme pas les autres joueurs: seul le joueur concerné voit un toast fort
        const currentPlayer = gameState.players[gameState.currentPlayerIndex];
        // Ne pas toasteur pour Mystère (cooldown/indisponible) — l'UI affiche un tooltip et un badge
        if (categoryId === 'mystery') {
          return;
        }
        if (currentPlayer && currentPlayer.id === playerId) {
          toast({
            title: t('blockedCategoryTooltip') || 'Catégorie bloquée',
            description: message || t('categoryBlocked') || 'Cette catégorie est temporairement bloquée.',
            status: 'warning',
            duration: 3500,
            isClosable: true,
            position: 'top',
          });
        }
      };

      const handleMysteryAttackError = (data: any) => {
        if (!data?.message) return;
        toast({
          title: t('mysteryAttackBlocked') || 'Attaque mystère impossible',
          description: data.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      };

      const handlePlayerDisconnected = ({ playerName, playerIndex }: { playerName: string; playerIndex: number }) => {
        console.log(`📤 Joueur ${playerName} déconnecté`);
        setGameState(prev => {
          const newPlayers = [...prev.players];
          if (newPlayers[playerIndex]) {
            newPlayers[playerIndex] = { 
              ...newPlayers[playerIndex], 
              isConnected: false, 
              name: `${newPlayers[playerIndex].name.replace(' (Déconnecté)', '')} (Déconnecté)`
            };
          }
          return { ...prev, players: newPlayers };
        });
      };

      const handlePlayerReconnected = ({ playerName, playerIndex }: { playerName: string; playerIndex: number }) => {
        console.log(`📥 Joueur ${playerName} reconnecté`);
        setGameState(prev => {
          const newPlayers = [...prev.players];
          if (newPlayers[playerIndex]) {
            newPlayers[playerIndex] = { 
              ...newPlayers[playerIndex], 
              isConnected: true, 
              name: playerName
            };
          }
          return { ...prev, players: newPlayers };
        });
      };

      const handleGameResetToLobby = (data: { lobbyCode: string; message: string }) => {
        console.log('🏠 Partie réinitialisée, retour au lobby');
        // Demander à l'App de gérer l'état + navigation
        onBackToLobby?.(data?.lobbyCode || lobbyCode);
      };

      socket.on('game-started', handleGameStarted);
      socket.on('game-state-update', handleGameStateUpdate);
      socket.on('players-update', handlePlayersUpdate);
      socket.on('question-received', handleQuestionReceived);
      socket.on('answer-result', handleAnswerResult);
      socket.on('mystery-target-selected', handleMysteryTargetSelected);
      socket.on('mystery-attack-result', (data: any) => {
        console.log('🎯 ===== ÉVÉNEMENT MYSTERY-ATTACK-RESULT REÇU =====');
        console.log('🎯 Données brutes reçues:', JSON.stringify(data, null, 2));
        console.log('🎯 Type de données:', typeof data);
        console.log('🎯 Keys des données:', Object.keys(data || {}));
        console.log('🎯 showPowerRoulette dans les données:', data?.showPowerRoulette);
        console.log('🎯 Timestamp:', new Date().toISOString());
        handleMysteryAttackResult(data);
        console.log('🎯 ===== FIN TRAITEMENT MYSTERY-ATTACK-RESULT =====');
      });
      socket.on('ping-test', (data: any) => {
        console.log('🧪 ===== PING TEST REÇU =====');
        console.log('🧪 Données du ping:', JSON.stringify(data, null, 2));
        console.log('🧪 Timestamp réception:', new Date().toISOString());
        console.log('🧪 ===== FIN PING TEST =====');
      });
      socket.on('game-ended-manually', handleGameEndedManually);
  socket.on('category-blocked', handleCategoryBlocked);
  socket.on('mystery-attack-error', handleMysteryAttackError);
      socket.on('player-disconnected', handlePlayerDisconnected);
      socket.on('player-reconnected', handlePlayerReconnected);
      socket.on('game-reset-to-lobby', handleGameResetToLobby);

      // Timers: start/expire events from server
      socket.on('category-timer-started', (data: any) => {
        // data: { playerId, seconds, deadline }
        setQuestionTimer(null);
        setCategoryTimer({ deadline: data.deadline, seconds: data.seconds, playerId: data.playerId });
      });
      socket.on('category-timer-expired', (_data: any) => {
        setCategoryTimer(null);
      });
      socket.on('question-timer-started', (data: any) => {
        // data: { playerId, seconds, deadline, categoryId }
        setQuestionTimer({ deadline: data.deadline, seconds: data.seconds, playerId: data.playerId, categoryId: data.categoryId });
      });
      socket.on('question-timer-expired', (_data: any) => {
        setQuestionTimer(null);
      });

      return () => {
        socket.off('game-started', handleGameStarted);
        socket.off('game-state-update', handleGameStateUpdate);
        socket.off('players-update', handlePlayersUpdate);
        socket.off('question-received', handleQuestionReceived);
        socket.off('answer-result', handleAnswerResult);
        socket.off('mystery-target-selected', handleMysteryTargetSelected);
        socket.off('mystery-attack-result', handleMysteryAttackResult);
        socket.off('game-ended-manually', handleGameEndedManually);
  socket.off('category-blocked', handleCategoryBlocked);
  socket.off('mystery-attack-error', handleMysteryAttackError);
        socket.off('player-disconnected', handlePlayerDisconnected);
        socket.off('player-reconnected', handlePlayerReconnected);
        socket.off('game-reset-to-lobby', handleGameResetToLobby);
        socket.off('category-timer-started');
        socket.off('category-timer-expired');
        socket.off('question-timer-started');
        socket.off('question-timer-expired');
      };
    }
  }, [socket, lobbyCode, currentUser, t, gameState.streakToWin, toast]);

  // Reset the forfeit guard on new question/turn
  useEffect(() => {
    forfeitSentRef.current = false;
  }, [gameState.currentQuestion?.id, gameState.currentPlayerIndex]);

  // Anti-cheat: if it's my turn and a question is active, leaving the tab/window auto-submits a wrong answer
  useEffect(() => {
    if (!socket) return;
    // Respect lobby rules toggle; default enabled if undefined
    const antiCheatEnabled = (rules as any)?.enableAntiCheat !== false;
    if (!antiCheatEnabled) return;
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    const isMyTurn = currentPlayer && currentPlayer.name === currentUser.displayName;
    const questionActive = !!gameState.currentQuestion;
    if (!isMyTurn || !questionActive) return;

    const triggerForfeit = () => {
      if (forfeitSentRef.current) return;
      // Only act if still my turn and question active at the moment of blur/hide
      const cur = gameState.players[gameState.currentPlayerIndex];
      const stillMyTurn = cur && cur.name === currentUser.displayName;
      if (!stillMyTurn || !gameState.currentQuestion) return;
      forfeitSentRef.current = true;
      const isMystery = gameState.currentCategory === 'mystery';
      socket.emit('answer-question', {
        lobbyCode,
        answer: 'a tenter de tricher en changeant de fenêtre',
        isCorrect: false,
        isMysteryAttack: isMystery,
        targetPlayerId: isMystery ? mysteryTargetPlayerId : undefined
      });
      // Optional user feedback (only for the local player)
      try {
        toast({
          title: t('antiCheat.forfeitTitle') || 'Réponse annulée',
          description: t('antiCheat.forfeitDesc') || "Sortie de l'onglet pendant la question: réponse comptée fausse.",
          status: 'warning',
          duration: 2500,
          isClosable: true,
          position: 'top'
        });
      } catch {}
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') triggerForfeit();
    };
    const onBlur = () => {
      // window blur also counts as leaving focus
      triggerForfeit();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    };
    // Intentionally depend on identifiers for current question/turn and user
  }, [socket, (rules as any)?.enableAntiCheat, gameState.currentQuestion?.id, gameState.currentPlayerIndex, gameState.currentCategory, currentUser.displayName, lobbyCode, mysteryTargetPlayerId, t, toast]);

  // Gérer la sélection d'un pouvoir dans la roulette
  const handlePowerSelected = (power: any) => {
    console.log('🎰 Pouvoir sélectionné:', power);
    
    // Protection contre les doubles appels
    if (!socket || !powerRouletteData) {
      console.log('🎰 ❌ Socket ou powerRouletteData manquant, appel ignoré');
      return;
    }
    // Seul l'attaquant doit pouvoir émettre l'événement d'application du pouvoir
    const attackerName = powerRouletteData.attackerPlayerName;
    if (currentUser?.displayName !== attackerName) {
      console.log('⛔ Ignoré: seul l’attaquant doit émettre power-roulette-result');
      // On laisse l’UI afficher le résultat mais on n’envoie rien
      setShowPowerRoulette(false);
      setPowerRouletteData(null);
      return;
    }
    
    // Marquer que nous sommes en train de traiter pour éviter les doubles appels
    if (powerRouletteData.processing) {
      console.log('🎰 ❌ Déjà en cours de traitement, appel ignoré');
      return;
    }
    
    // Marquer comme en cours de traitement
    setPowerRouletteData({ ...powerRouletteData, processing: true });
    
    // Envoyer le pouvoir sélectionné au serveur
    console.log('🎰 Envoi de power-roulette-result au serveur...');
  socket.emit('power-roulette-result', {
      lobbyCode,
      power: Math.abs(power.value), // Toujours envoyer la valeur absolue
      powerName: power.name,
      powerId: power.id // Ajouter l'ID pour différencier bonus/malus
    });
    
    // Fermer la roulette
    setShowPowerRoulette(false);
    setPowerRouletteData(null);
    
    // Afficher un message de confirmation
    toast({
      title: '🎰 Pouvoir appliqué !',
      description: `${power.name} - ${power.description}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Fonction pour sélectionner une catégorie pour une attaque mystère
  const handleSelectCategoryForMystery = async (targetPlayerId: number) => {
    console.log('🔮 Attaque mystère contre joueur:', targetPlayerId, '- Mode:', rules.mysteryAttackMode);
    
    // Mettre à jour l'état pour indiquer qu'une question mystère est en cours de chargement
    setGameState(prev => ({
      ...prev,
      isLoading: true,
      currentCategory: 'mystery',
    }));
    
    // Envoyer la demande de question mystère directement au serveur Socket.IO
    if (socket) {
      const dataToSend = { 
        lobbyCode, 
        categoryId: 'mystery',
        targetPlayerId,
        mysteryAttackMode: rules.mysteryAttackMode || 'classic', // Passer le mode sélectionné
        // Pas de question prépparée - le serveur va la récupérer
      };
      socket.emit('select-category', dataToSend);
    }
    
    setShowMysteryTargetSelector(false);
    setMysteryTargetSelected(true);
  };

  // Fonction pour sélectionner une catégorie
  const handleSelectCategory = async (categoryId: string) => {
    // Vérifier si c'est une catégorie mystère
    const category = gameState.categories.find(c => c.id === categoryId);
    if (category && category.is_mystery) {
      setShowMysteryTargetSelector(true);
      return;
    }
    
    // Mettre à jour l'état pour indiquer qu'une question est en cours de chargement
    setGameState(prev => ({
      ...prev,
      isLoading: true,
      currentCategory: categoryId,
    }));
    
    // Envoyer la demande de question directement au serveur Socket.IO
    // Le serveur se chargera de récupérer une question qui respecte les règles et qui n'a pas déjà été posée
    if (socket) {
      socket.emit('select-category', { 
        lobbyCode, 
        categoryId,
        // Pas de question prépparée - le serveur va la récupérer
      });
    }
  };

  // Fonction pour répondre à une question
  const handleAnswerQuestion = async (answer: string, originalIndex?: number) => {
    if (!gameState.currentQuestion) return;
    
    try {
      // Obtenir les options de la question (même logique que MultiplayerQuestionCard)
      const getQuestionOptions = () => {
        const question = gameState.currentQuestion;
        
        if (question.options && Array.isArray(question.options)) {
          return question.options;
        }
        
        if (question.option_1) {
          return [question.option_1, question.option_2, question.option_3, question.option_4].filter(Boolean);
        }
        
        if (question.answers && Array.isArray(question.answers)) {
          return question.answers;
        }
        
        return [];
      };
      
      const questionOptions = getQuestionOptions();
      
      // Calculer l'index de la réponse (1-based pour l'API)
      // Si originalIndex est fourni, l'utiliser, sinon utiliser indexOf comme fallback
      const answerIndex = originalIndex !== undefined 
        ? originalIndex + 1  // Convertir en 1-based pour l'API
        : questionOptions.indexOf(answer) + 1;
      
      // Si c'est une question simulée (mock), vérifier localement sans appeler l'API
      if ((gameState.currentQuestion as any).mock) {
        const correctIdx =
          (gameState.currentQuestion as any).correct_answer_index ??
          (((gameState.currentQuestion as any).correctAnswer
            ? questionOptions.indexOf((gameState.currentQuestion as any).correctAnswer) + 1
            : 1));

        const providedIdx = originalIndex !== undefined ? originalIndex + 1 : answerIndex;
        const isCorrect = providedIdx === correctIdx;

        if (socket) {
          const dataToSend = {
            lobbyCode,
            answer,
            originalIndex,
            isCorrect,
            isMysteryAttack: gameState.currentCategory === 'mystery',
            targetPlayerId: gameState.currentCategory === 'mystery' ? mysteryTargetPlayerId : undefined,
          };
          socket.emit('answer-question', dataToSend);
          if (gameState.currentCategory === 'mystery') setMysteryTargetPlayerId(null);
        }
        return;
      }

      // Vérifier la réponse via l'API
      console.log('📡 Vérification de la réponse via API...');
      
      // Créer un timeout pour éviter que la requête se bloque
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout: API verification took too long')), 10000); // 10 secondes
      });
      
      const fetchPromise = fetch(`${API_URL}/api/verify-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: Number(gameState.currentQuestion.id),
          answerIndex: answerIndex
        })
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]) as Response;
      
      if (!response.ok) {
        console.error('❌ Erreur lors de la vérification:', response.status);
        const errorText = await response.text();
        console.error('❌ Détails de l\'erreur:', errorText);
        return;
      }
      
      const verificationResult = await response.json();
      const { isCorrect } = verificationResult;
      
      // Envoyer le résultat au serveur Socket.IO
      if (socket) {
        // Pour les attaques mystère, on envoie des informations spéciales
        const dataToSend = {
          lobbyCode, 
          answer, 
          originalIndex, // Ajouter l'index original pour que le serveur puisse retrouver la vraie réponse
          isCorrect,
          isMysteryAttack: gameState.currentCategory === 'mystery',
          targetPlayerId: gameState.currentCategory === 'mystery' ? mysteryTargetPlayerId : undefined
        };
        
        socket.emit('answer-question', dataToSend);
        
        // Réinitialiser le mysteryTargetPlayerId après l'envoi
        if (gameState.currentCategory === 'mystery') {
          setMysteryTargetPlayerId(null);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors de la vérification de la réponse:', error);
    }
  };

  // Vérifier si l'utilisateur actuel est l'hôte de la partie
  const isCurrentUserHost = () => {
    return hostName === currentUser.displayName;
  };

  // Fonction pour terminer la partie manuellement (seulement pour l'hôte)
  const handleEndGameManually = () => {
    if (!isCurrentUserHost()) {
      toast({
        title: t('error'),
        description: t('game.onlyHostCanEndGame'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (endGameManually && lobbyCode) {
      // 🔊 Son de fin de partie
      playSound('gameLose');
      
      endGameManually(lobbyCode, currentUser.displayName);
      
      toast({
        title: t('game.gameEnded'),
        description: t('game.gameEndedManually'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Affichage de chargement
  if (gameState.isLoading) {
    return (
      <Flex justify="center" align="center" h="60vh">
        <Spinner size="xl" color="var(--bleu-electrique)" thickness="4px" />
      </Flex>
    );
  }

  // Déterminer quel composant afficher (même logique que le mode local)
  const renderContent = () => {
    if (gameState.isGameOver) {
      return (
        <MultiplayerGameOver
          gameState={gameState}
          lobbyCode={lobbyCode}
          onBackToLobby={(code?: string) => onBackToLobby?.(code || lobbyCode)}
          onQuit={() => onBackToLobby?.()}
        />
      );
    }
    if (gameState.currentQuestion) {
      return (
        <MultiplayerQuestionCard 
          gameState={gameState} 
          onAnswerQuestion={handleAnswerQuestion}
          currentUser={currentUser}
          answerResult={answerResult}
          questionTimer={questionTimer}
        />
      );
    }
    // Par défaut, afficher la grille des catégories
    return (
      <MultiplayerCategoryGrid 
        gameState={gameState} 
        onSelectCategory={handleSelectCategory}
        onOpenMysterySelector={() => setShowMysteryTargetSelector(true)}
        currentUser={currentUser}
        categoryTimer={categoryTimer}
      />
    );
  };

  return (
    <Box mx={-5} mt={-5} mb={-5} w="calc(100% + 40px)">
      <VStack spacing={8} align="center" w="full" justify="center" minH="100vh">
        {/* Contrôles en haut - mobile friendly */}
        <Box 
          position="fixed" 
          top={{ base: 2, md: 4 }} 
          right={{ base: 2, md: 4 }} 
          left={{ base: 2, md: "auto" }}
          zIndex={1000}
          display="flex"
          justifyContent={{ base: "space-between", md: "flex-end" }}
          alignItems="center"
          maxW={{ base: "calc(100vw - 16px)", md: "auto" }}
          px={{ base: 2, md: 0 }}
        >
          <HStack spacing={{ base: 1, md: 2 }}>
            {/* Bouton pour terminer la partie (seulement pour l'hôte) */}
            {isCurrentUserHost() && !gameState.isGameOver && (
              <Tooltip label={t('game.endGameManually')} hasArrow>
                <IconButton
                  aria-label={t('game.endGameManually')}
                  icon={<FiFlag />}
                  size="sm"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  onClick={handleEndGameManually}
                />
              </Tooltip>
            )}
            <SoundControl 
              size="sm" 
              variant="compact" 
              iconColor="white" 
            />
            <HelpPopover />
          </HStack>
        </Box>
        
        {/* Contenu principal centré normalement */}
        {renderContent()}
        
        {/* Sélecteur de cible mystère */}
        <MultiplayerMysteryTargetSelector
          isOpen={showMysteryTargetSelector}
          onClose={() => setShowMysteryTargetSelector(false)}
          onTargetSelected={() => setMysteryTargetSelected(true)}
          gameState={gameState}
        />
        
        {/* Nouvelle roulette des pouvoirs */}
        <PowerRoulette
          isOpen={showPowerRoulette}
          onClose={() => setShowPowerRoulette(false)}
          onPowerSelected={handlePowerSelected}
          maxStreakLoss={powerRouletteData?.maxStreakLoss || 0}
          targetPlayerName={powerRouletteData?.targetPlayerName || ''}
          attackerPlayerName={powerRouletteData?.attackerPlayerName || ''}
          isAttackerWinner={powerRouletteData?.isAttackerWinner || false}
          preSelectedPower={powerRouletteData?.preSelectedPower}
          rouletteConfig={(gameState as any)?.rules?.roulettePowers || (rules as any)?.roulettePowers}
          seedKey={`${lobbyCode}|${powerRouletteData?.attackerPlayerName}|${powerRouletteData?.targetPlayerName}|${powerRouletteData?.maxStreakLoss}|${powerRouletteData?.preSelectedPower?.id || ''}`}
        />
      </VStack>
    </Box>
  );
};

export default MultiplayerGameWrapper;
